import React from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

const DEFAULT_SCALE = [0, 25, 50, 75, 100];
const IDENTITY = (v) => v;

const clamp = (n) => Math.max(0, Math.min(100, n));

const HyroBenchmarkBar = ({
  value = 0,
  markers = [],
  scale = DEFAULT_SCALE,
  showScaleLabels = true,
  loading = false,
  currentLabel,
  formatValue = IDENTITY
}) => {
  if (loading) {
    return (
      <Stack gap={0.5} width="100%">
        <Skeleton variant="text" width={120} />
        <Skeleton variant="rectangular" width="100%" height={12} sx={{ borderRadius: 0.25 }} />
        {showScaleLabels && <Skeleton variant="text" width="100%" />}
      </Stack>
    );
  }

  const labeledMarkers = markers.filter((m) => m.label);
  const hasLegend = !!currentLabel || labeledMarkers.length > 0;

  const bar = (
    <Track>
      <Fill style={{ width: `${clamp(value)}%` }} />
      {markers.map((marker, i) => (
        <Marker
          key={`marker-${i}`}
          style={{ left: `${clamp(marker.value)}%`, backgroundColor: marker.color }}
        />
      ))}
    </Track>
  );

  return (
    <Stack gap={0.5} width="100%">
      {hasLegend && (
        <LegendRow>
          {labeledMarkers.map((marker, i) => (
            <LegendItem key={`legend-marker-${i}`}>
              <MarkerSwatch style={{ backgroundColor: marker.color }} />
              <LegendText>{marker.label}</LegendText>
              <LegendText>{formatValue(marker.value)}</LegendText>
            </LegendItem>
          ))}
          {currentLabel && (
            <LegendItem>
              <FillSwatch />
              <LegendText>{currentLabel}</LegendText>
              <LegendText>{formatValue(value)}</LegendText>
            </LegendItem>
          )}
        </LegendRow>
      )}
      {bar}
      {showScaleLabels && (
        <ScaleRow>
          {scale.map((tick) => (
            <ScaleLabel key={tick}>{tick}{tick === 0 ? "" : "%"}</ScaleLabel>
          ))}
        </ScaleRow>
      )}
    </Stack>
  );
};

const Track = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: 12,
  borderRadius: 2,
  backgroundColor: theme.palette.system[300],
  overflow: "hidden"
}));

const Fill = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  height: "100%",
  backgroundColor: theme.palette.secondary[400]
}));

const Marker = styled(Box)({
  position: "absolute",
  top: 0,
  height: "100%",
  width: 2,
  transform: "translateX(-1px)"
});

const ScaleRow = styled(Stack)({
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%"
});

const ScaleLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text[700],
  fontSize: 9,
  lineHeight: 1.4
}));

const LegendRow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(1.5)
}));

const LegendItem = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(0.5)
}));

const LegendText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text[700],
  fontSize: 9,
  lineHeight: "normal",
  textAlign: "center",
  whiteSpace: "nowrap"
}));

const FillSwatch = styled(Box)(({ theme }) => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  backgroundColor: theme.palette.secondary[400],
  flexShrink: 0
}));

const MarkerSwatch = styled(Box)({
  width: 2,
  height: 8,
  flexShrink: 0
});

export default HyroBenchmarkBar;
